import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bookmark } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import ScoreBadge from "@/components/ScoreBadge";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import ScoreExplanation from "@/components/ScoreExplanation";
import PriceChart from "@/components/PriceChart";
import DataQualityBadge from "@/components/DataQualityBadge";
import LifecycleBadge from "@/components/LifecycleBadge";
import OpportunityBadge from "@/components/OpportunityBadge";
import EvidencePanel from "@/components/EvidencePanel";
import AIReport, { useAIReport } from "@/components/AIReport";
import AIAnalyst from "@/components/AIAnalyst";
import ContrarianView from "@/components/ContrarianView";
import SignalActionBar from "@/components/SignalActionBar";
import InfoTip from "@/components/InfoTip";
import { fmtPct, fmtNum, fmtUSD, timeAgo, truncWallet } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DEFAULT_WEIGHTS = {
  trader_quality: 0.25,
  consensus: 0.20,
  conviction: 0.15,
  remaining_edge: 0.15,
  liquidity: 0.10,
  timing: 0.10,
  data_completeness: 0.05,
};

const SCORE_COMPONENT_TIPS = {
  trader_quality:   "How highly ranked the participating traders are (0–100). Weighted by their historical performance and category expertise.",
  consensus:        "How strongly tracked traders agree on this direction. Higher = more unanimity + higher-quality voices.",
  conviction:       "Position sizing intensity. Larger positions relative to a trader's normal exposure = higher conviction.",
  remaining_edge:   "How much of the original entry-to-target gap is still open. 100% = full edge left; 0% = already priced in.",
  liquidity:        "How much you could theoretically trade before moving price. Higher liquidity = lower slippage cost.",
  timing:           "How fresh the underlying trader activity is. Recent moves score higher; stale positioning is discounted.",
  data_completeness:"How much of the required evidence we have on file. Missing fields (bid/ask, trader history) reduce this score.",
};

export default function SignalDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["signal", id],
    queryFn: async () => (await api.get(`/signals/${id}`)).data,
  });
  const { data: wls } = useQuery({
    queryKey: ["watchlists-signal"],
    queryFn: async () => (await api.get("/watchlists")).data,
  });
  React.useEffect(() => {
    track("signal_opened", { target_kind: "signal", target_id: id });
    api.post("/onboarding/progress/inspected_signal").catch(() => {});
  }, [id]);
  const narrateMut = useAIReport(`/intel/signals/${id}/narrative`, "signal");
  const [narrative, setNarrative] = React.useState(null);
  const watchMut = useMutation({
    mutationFn: async () => {
      const marketId = data?.signal?.market_id;
      if (!marketId) throw new Error("No market attached to this signal");
      const wl = (wls || []).find((w) => w.type === "market") || (wls || [])[0];
      if (!wl) throw new Error("No market watchlist found");
      await api.post(`/watchlists/${wl.id}/items/${marketId}`);
      return wl;
    },
    onSuccess: (wl) => {
      qc.invalidateQueries({ queryKey: ["watchlists-signal"] });
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      qc.invalidateQueries({ queryKey: ["watchlist-enriched", wl.id] });
      toast.success("Market added to watchlist");
    },
    onError: (err) => toast.error(err?.message || "Could not add to watchlist"),
  });
  if (isLoading) return <div className="text-sm text-zinc-500">Loading signal…</div>;
  if (isError || !data) return <div className="text-sm text-rose-400">Signal not found.</div>;
  const s = data.signal;
  const m = data.market;
  const signalMarketId = s.market_id;
  const inWatchlist = !!signalMarketId && (wls || []).some((w) => w.type === "market" && w.item_ids?.includes(signalMarketId));

  return (
    <div className="space-y-6" data-testid="signal-detail">
      <div>
        <Link to="/app/signals" className="text-xs text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Signals
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mt-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{(s.signal_type || "signal").replaceAll("_", " ")} · {s.market_category || "Uncategorized"}</div>
            <h1 className="font-heading text-2xl font-bold text-zinc-100 leading-tight mt-1">
              {m?.title || s.market_title || s.market_id}
            </h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 text-xs text-zinc-400">
              <span>Direction: <span className="text-zinc-200">{s.outcome}</span></span>
              <span>·</span>
              <span>Detected {timeAgo(s.detected_at)}</span>
              <DataQualityBadge status={s.data_quality || "partial"} />
              <LifecycleBadge status={s.lifecycle_status} testid="signal-lifecycle" />
            </div>
          </div>
          <div className="text-right">
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <OpportunityBadge rating={s.opportunity_rating} size="lg" testid="signal-opportunity-rating" />
              <ConfidenceBadge confidence={s.confidence} testid="signal-detail-confidence" />
              <ScoreBadge score={s.overall_score} size="lg" testid="signal-overall-score" />
            </div>
            <div className="mt-1">
              <ScoreExplanation
                score={s.overall_score}
                components={s.scores}
                weights={DEFAULT_WEIGHTS}
                confidence={0.75}
                penalties={s.penalties}
                formulaVersion={s.formula_version}
              />
            </div>
            <Button
              size="sm"
              onClick={() => watchMut.mutate()}
              disabled={inWatchlist || watchMut.isPending || !signalMarketId}
              data-testid="btn-signal-watchlist-add"
              className="mt-3 h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
            >
              <Bookmark size={11} className="mr-1" /> {inWatchlist ? "Watching market" : "Add market to watchlist"}
            </Button>
          </div>
        </div>

        {/* Copilot actions */}
        <div className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-zinc-800/70">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Your call</span>
          <SignalActionBar
            signalId={s.id}
            currentAction={s.user_action}
            testid="signal-detail-actions"
          />
        </div>
      </div>

      {/* Actionability */}
      <section className="border border-zinc-800 rounded-md p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-heading font-semibold text-zinc-100">{s.actionability_label}</h2>
          <div className="text-[11px] text-zinc-500 flex items-center gap-1">
            <span>Remaining edge</span>
            <InfoTip content="Percentage of the original entry-to-target price gap that is still open. 100% = full edge left; 0% = market has already run to where the traders bought in." testid="tip-remaining-edge" />
            :{" "}
            <span className="font-mono-num text-zinc-200">
              {s.remaining_edge_pct != null ? `${Math.round(s.remaining_edge_pct * 100)}%` : "—"}
            </span>
          </div>
        </div>
        <div className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.explanation}</div>
      </section>

      {/* AI Analyst — interactive reasoning */}
      <AIAnalyst endpoint={`/intel/analyst/signal/${id}`} testid="signal-ai-analyst" />

      {/* Contrarian View */}
      <ContrarianView endpoint={`/intel/signals/${id}/contrarian`} testid="signal-contrarian" />

      {/* AI Opportunity Narrative — Why This / Why Now / Why These Traders / Why Care */}
      <AIReport
        title="Opportunity Narrative · The 4 Whys"
        report={narrative}
        isGenerating={narrateMut.isPending}
        onGenerate={() => narrateMut.mutateAsync().then(setNarrative).catch(() => {})}
        ctaLabel="Generate narrative"
        testid="opportunity-narrative"
        quotaEndpoint="signal_narrative"
      />

      {/* Evidence Panel */}
      <EvidencePanel signal={s} market={m} consensus={data.consensus}
                     traders={data.participating_traders} />

      {/* Score components */}
      <section>
        <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Score decomposition</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(s.scores || {}).map(([k, v]) => (
            <div key={k} className="border border-zinc-800 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>{k.replaceAll("_", " ")}</span>
                <InfoTip
                  content={SCORE_COMPONENT_TIPS[k] || "One component of the overall signal score (0–100)."}
                  testid={`tip-score-${k}`}
                />
              </div>
              <div className="text-lg font-mono-num text-zinc-100 mt-1">{Math.round(v)}</div>
              <div className="h-1.5 rounded-full bg-zinc-800 mt-2 overflow-hidden">
                <div
                  className="h-full bg-zinc-300"
                  style={{ width: `${Math.max(4, Math.min(100, v))}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Participating traders */}
      <section>
        <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Participating traders</h2>
        <div className="border border-zinc-800 rounded-sm overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Trader</th>
                <th className="text-left px-3 py-2">Wallet</th>
                <th className="text-right px-3 py-2">Score</th>
              </tr>
            </thead>
            <tbody className="stagger-in">
              {data.participating_traders.length === 0 && (
                <tr><td colSpan={3} className="text-xs text-zinc-500 text-center py-6">No participating traders.</td></tr>
              )}
              {data.participating_traders.map((t) => (
                <tr key={t.id} className="border-t border-zinc-800/70 hover:bg-zinc-900/40 transition-colors">
                  <td className="px-3 py-2">
                    <Link to={`/app/traders/${t.id}`} className="text-zinc-100 hover:underline">
                      {t.public_username || truncWallet(t.wallet_address)}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs text-zinc-500">{truncWallet(t.wallet_address)}</td>
                  <td className="px-3 py-2 text-right"><ScoreBadge score={t.score} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Price history since detection */}
      {data.price_history?.length > 0 && (
        <section>
          <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Price movement since detection</h2>
          <PriceChart history={data.price_history} />
        </section>
      )}

      {/* Market meta */}
      {m && (() => {
        // Signal-level bid/ask/spread aren't always populated. Fall back to
        // per-outcome data from the market for the signal's direction.
        const outcomes = m.outcomes || [];
        const activeOutcome = outcomes.find(
          (o) => o.name === s.outcome || o.token_id === s.token_id
        ) || outcomes[0];
        const bestBid = s.best_bid ?? activeOutcome?.best_bid;
        const bestAsk = s.best_ask ?? activeOutcome?.best_ask;
        const spread  = s.spread   ?? activeOutcome?.spread;
        return (
        <section className="border border-zinc-800 rounded-md p-4 text-sm text-zinc-400" data-testid="market-microstructure">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Volume</div>
              <div className="text-zinc-100 font-mono-num">{fmtUSD(m.volume)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Liquidity</div>
              <div className="text-zinc-100 font-mono-num">{fmtUSD(m.liquidity)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Best bid / ask</span>
                <InfoTip content="Best bid and ask on the order book for this outcome. Falls back to the market's most recent per-outcome CLOB reading if the signal snapshot is empty." testid="tip-bid-ask" />
              </div>
              <div className="text-zinc-100 font-mono-num" data-testid="signal-bid-ask">
                {bestBid != null ? fmtPct(bestBid) : "—"} /{" "}
                {bestAsk != null ? fmtPct(bestAsk) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <span>Spread</span>
                <InfoTip content="Difference between best ask and best bid, in probability points." testid="tip-spread" />
              </div>
              <div className="text-zinc-100 font-mono-num" data-testid="signal-spread">
                {spread != null ? `${(spread * 100).toFixed(2)}pp` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">End date</div>
              <div className="text-zinc-100">{m.end_date ? new Date(m.end_date).toLocaleDateString() : "—"}</div>
            </div>
            <div className="md:col-span-3">
              <Link to={`/app/markets/${m.polymarket_market_id}`} className="text-zinc-300 hover:underline text-sm inline-flex items-center gap-1">
                Full market view →
              </Link>
            </div>
          </div>
        </section>
        );
      })()}
    </div>
  );
}
